import React from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Stack,
  Chip,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SpeedIcon from "@mui/icons-material/Speed";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import LaunchIcon from "@mui/icons-material/Launch";
import BusinessIcon from "@mui/icons-material/Business";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PsychologyIcon from "@mui/icons-material/Psychology";
import StorageIcon from "@mui/icons-material/Storage";
import CloudIcon from "@mui/icons-material/Cloud";
import DevicesIcon from "@mui/icons-material/Devices";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { motion } from "framer-motion";
import { useCases } from "../../hooks/useCases";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import EditableImage from "../../components/ui/EditableImage";
import { Link } from "react-router-dom";
import SEO from "../../components/seo/SEO";

const getIconForType = (type) => {
  const t = type?.toLowerCase() || "";
  if (t.includes("machine learning") || t.includes("ai"))
    return <PsychologyIcon sx={{ color: "#0FA3A3" }} />;
  if (t.includes("data") || t.includes("big data"))
    return <StorageIcon sx={{ color: "#0FA3A3" }} />;
  if (t.includes("cloud") || t.includes("infrastructure"))
    return <CloudIcon sx={{ color: "#0FA3A3" }} />;
  if (t.includes("mobile") || t.includes("app"))
    return <DevicesIcon sx={{ color: "#0FA3A3" }} />;
  if (t.includes("saas") || t.includes("software"))
    return <SettingsSuggestIcon sx={{ color: "#0FA3A3" }} />;
  if (t.includes("poc") || t.includes("enterprise"))
    return <BusinessIcon sx={{ color: "#0FA3A3" }} />;
  return <TrendingUpIcon sx={{ color: "#0FA3A3" }} />;
};

const ServicesPage = () => {
  const { t, i18n } = useTranslation();
  const { data: cases, isLoading: loadingCases } = useCases({
    isPublished: true,
    language: i18n.language,
  });

  const pricingPacks = [
    {
      title: t("services.pricing.packs.advisory.title"),
      subtitle: t("services.pricing.packs.advisory.subtitle"),
      price: t("services.pricing.packs.advisory.price"),
      period: t("services.pricing.packs.advisory.period"),
      icon: <SpeedIcon sx={{ fontSize: 32, color: "#D4AF37" }} />,
      description: t("services.pricing.packs.advisory.description"),
      features: t("services.pricing.packs.advisory.features", {
        returnObjects: true,
      }),
      cta: t("services.pricing.packs.advisory.cta"),
      highlight: false,
    },
    {
      title: t("services.pricing.packs.build.title"),
      subtitle: t("services.pricing.packs.build.subtitle"),
      price: t("services.pricing.packs.build.price"),
      period: t("services.pricing.packs.build.period"),
      icon: <RocketLaunchIcon sx={{ fontSize: 32, color: "#0FA3A3" }} />,
      description: t("services.pricing.packs.build.description"),
      features: t("services.pricing.packs.build.features", {
        returnObjects: true,
      }),
      cta: t("services.pricing.packs.build.cta"),
      highlight: true,
    },
    {
      title: t("services.pricing.packs.scale.title"),
      subtitle: t("services.pricing.packs.scale.subtitle"),
      price: t("services.pricing.packs.scale.price"),
      period: t("services.pricing.packs.scale.period"),
      icon: <AutoGraphIcon sx={{ fontSize: 32, color: "#D4AF37" }} />,
      description: t("services.pricing.packs.scale.description"),
      features: t("services.pricing.packs.scale.features", {
        returnObjects: true,
      }),
      cta: t("services.pricing.packs.scale.cta"),
      highlight: false,
    },
  ];

  return (
    <Box
      component="main"
      sx={{ bgcolor: "#0B0F14", minHeight: "100vh", pb: 20 }}
    >
      <SEO 
        title={t('services.seo.title', { defaultValue: 'Services' })}
        description={t('services.seo.description', { defaultValue: "Services de consulting en Data Science et IA. Développement d'applications, Machine Learning, et accompagnement technique." })}
        keywords="consulting, data science, machine learning, développement, freelance, Paris"
      />
      {/* Header Section */}
      <Box
        sx={{
          py: 15,
          bgcolor: "#111827",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="overline"
              align="center"
              sx={{
                color: "#D4AF37",
                fontWeight: 700,
                letterSpacing: 3,
                display: "block",
                mb: 2,
              }}
            >
              {t("services.header.overline")}
            </Typography>
            <Typography
              variant="h1"
              align="center"
              sx={{
                color: "white",
                mb: 3,
                fontFamily: "Philosopher",
                fontSize: { xs: "2.5rem", md: "3.5rem" },
              }}
            >
              {t("services.header.titlePrefix")} <br />
              <span style={{ color: "#0FA3A3" }}>
                {t("services.header.titleHighlight")}
              </span>{" "}
              {t("services.header.titleSuffix")}
            </Typography>
            <Typography
              variant="h6"
              align="center"
              sx={{
                color: "#9CA3AF",
                fontWeight: 300,
                lineHeight: 1.6,
                maxWidth: "700px",
                mx: "auto",
              }}
            >
              {t("services.header.subtitle")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -8 }}>
        {/* Main Pricing Cards */}
        <Grid container spacing={4} justifyContent="center">
          {pricingPacks.map((pack, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{ height: "100%" }}
              >
                <Card
                  sx={{
                    height: "100%",
                    bgcolor: "#1F2937",
                    borderRadius: "24px",
                    border: "1px solid",
                    borderColor: pack.highlight
                      ? "#0FA3A3"
                      : "rgba(255,255,255,0.05)",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "visible",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-12px)",
                      borderColor: pack.highlight ? "#0FA3A3" : "#D4AF37",
                      boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
                    },
                  }}
                >
                  {pack.highlight && (
                    <Chip
                      label={t("services.pricing.mostPopular")}
                      sx={{
                        position: "absolute",
                        top: -16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: "#0FA3A3",
                        color: "white",
                        fontWeight: 700,
                        px: 1,
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 5, flexGrow: 1 }}>
                    <Box
                      sx={{
                        mb: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      {pack.icon}
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ color: "white", fontWeight: 800 }}
                        >
                          {pack.title}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "#0FA3A3", fontWeight: 600 }}
                        >
                          {pack.subtitle}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: "#9CA3AF", mb: 4, lineHeight: 1.6 }}
                    >
                      {pack.description}
                    </Typography>

                    <Box
                      sx={{
                        mb: 6,
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{ color: "white", fontWeight: 800 }}
                      >
                        {pack.price}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: "#9CA3AF", fontWeight: 400 }}
                      >
                        {pack.period}
                      </Typography>
                    </Box>

                    <Stack spacing={2.5}>
                      {pack.features.map((feature, idx) => (
                        <Box
                          key={idx}
                          sx={{ display: "flex", alignItems: "start", gap: 2 }}
                        >
                          <CheckCircleOutlineIcon
                            sx={{ color: "#D4AF37", mt: 0.3 }}
                            fontSize="small"
                          />
                          <Typography
                            variant="body1"
                            sx={{ color: "#D4AF37", fontWeight: 500 }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>

                  <Box sx={{ p: 5, pt: 0 }}>
                    <Button
                      fullWidth
                      variant={pack.highlight ? "contained" : "outlined"}
                      component="a"
                      href="https://calendly.com/sanoualexandreism/30min"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        py: 2,
                        borderRadius: "12px",
                        fontWeight: 800,
                        fontSize: "1rem",
                        bgcolor: pack.highlight ? "#0FA3A3" : "transparent",
                        borderColor: pack.highlight
                          ? "#0FA3A3"
                          : "rgba(255,255,255,0.2)",
                        color: "white",
                        "&:hover": {
                          bgcolor: pack.highlight
                            ? "#0C8A8A"
                            : "rgba(255,255,255,0.05)",
                          borderColor: pack.highlight ? "#0C8A8A" : "#D4AF37",
                        },
                      }}
                    >
                      {pack.cta}
                    </Button>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Product-Based Section */}
        <Box sx={{ mt: 20 }}>
          <Box sx={{ mb: 8, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{ color: "white", mb: 2, fontFamily: "Philosopher" }}
            >
              {t("services.productBased.title")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#9CA3AF", maxWidth: "600px", mx: "auto" }}
            >
              {t("services.productBased.subtitle")}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 6,
                  bgcolor: "#111827",
                  borderRadius: "24px",
                  border: "1px solid rgba(255,255,255,0.05)",
                  height: "100%",
                  transition: "0.3s",
                  "&:hover": { borderColor: "#0FA3A3" },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: "white", fontWeight: 800, mb: 2 }}
                >
                  {t("services.productBased.poc.title")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#9CA3AF", mb: 4, lineHeight: 1.6 }}
                >
                  {t("services.productBased.poc.description")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#0FA3A3", fontWeight: 800 }}
                  >
                    {t("services.productBased.poc.price")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                    {t("services.productBased.poc.priceLabel")}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 6,
                  bgcolor: "#111827",
                  borderRadius: "24px",
                  border: "1px solid rgba(255,255,255,0.05)",
                  height: "100%",
                  transition: "0.3s",
                  "&:hover": { borderColor: "#D4AF37" },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: "white", fontWeight: 800, mb: 2 }}
                >
                  {t("services.productBased.mvp.title")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#9CA3AF", mb: 4, lineHeight: 1.6 }}
                >
                  {t("services.productBased.mvp.description")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#D4AF37", fontWeight: 800 }}
                  >
                    {t("services.productBased.mvp.price")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                    {t("services.productBased.mvp.priceLabel")}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Footer CTA */}
        <Box
          sx={{
            mt: 15,
            p: { xs: 6, md: 10 },
            background: "linear-gradient(135deg, #1F2937 0%, #111827 100%)",
            borderRadius: "32px",
            textAlign: "center",
            border: "1px solid rgba(255,255,255,0.05)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "white",
              mb: 4,
              fontFamily: "Philosopher",
              fontWeight: 700,
            }}
          >
            {t("services.footerCta.titlePrefix")}{" "}
            <span style={{ color: "#0FA3A3" }}>
              {t("services.footerCta.titleHighlight")}
            </span>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#9CA3AF",
              mb: 6,
              maxWidth: "600px",
              mx: "auto",
              fontSize: "1.1rem",
            }}
          >
            {t("services.footerCta.subtitle")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            component="a"
            href="https://calendly.com/sanoualexandreism/30min"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor: "#D4AF37",
              color: "#0B0F14",
              px: 8,
              py: 2.5,
              borderRadius: "14px",
              fontWeight: 800,
              fontSize: "1.2rem",
              textTransform: "none",
              boxShadow: "0 10px 20px rgba(212, 175, 55, 0.2)",
              "&:hover": {
                bgcolor: "#B8962E",
                transform: "translateY(-3px)",
                boxShadow: "0 15px 30px rgba(212, 175, 55, 0.3)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {t("services.footerCta.button")}
          </Button>
        </Box>

        {/* Case Studies Section */}
        <Box sx={{ mt: 20, mb: 10 }}>
          <Box sx={{ mb: 8, textAlign: "center" }}>
            <Typography
              variant="overline"
              sx={{ color: "#0FA3A3", fontWeight: 700, letterSpacing: 2 }}
            >
              {t("services.caseStudies.overline")}
            </Typography>
            <Typography
              variant="h2"
              sx={{ color: "white", mt: 1, fontFamily: "Philosopher" }}
            >
              {t("services.caseStudies.titlePrefix")}{" "}
              <span style={{ color: "#D4AF37" }}>
                {t("services.caseStudies.titleHighlight")}
              </span>
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#9CA3AF", maxWidth: "600px", mx: "auto", mt: 2 }}
            >
              {t("services.caseStudies.subtitle")}
            </Typography>
          </Box>

          <Grid container spacing={6}>
            {loadingCases ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  py: 10,
                }}
              >
                <CircularProgress sx={{ color: "#0FA3A3" }} />
              </Box>
            ) : (
              cases?.map((study, idx) => (
                <Grid item xs={12} md={6} key={study.id}>
                  <motion.div
                    initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                  >
                    <Card
                      sx={{
                        bgcolor: "#111827",
                        borderRadius: "28px",
                        overflow: "hidden",
                        border: "1px solid rgba(255,255,255,0.05)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        "&:hover": {
                          borderColor: "#D4AF37",
                          "& .case-image": { transform: "scale(1.05)" },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          height: "240px",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <EditableImage
                          src={
                            study.cover_image_url ||
                            "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80"
                          }
                          alt={study.title}
                          tableName="project_cases"
                          recordId={study.id}
                          imageField="cover_image_url"
                          sx={{ height: "100%", width: "100%" }}
                          imgSx={{
                            transition: "transform 0.6s ease",
                            filter: "brightness(0.7)",
                          }}
                          initialObjectFit={study.object_fit || "cover"}
                          initialObjectPosition={
                            study.object_position || "50% 50%"
                          }
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 20,
                            left: 20,
                            bgcolor: "rgba(15, 163, 163, 0.9)",
                            color: "white",
                            px: 2,
                            py: 0.5,
                            borderRadius: "8px",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            backdropFilter: "blur(4px)",
                            zIndex: 1,
                          }}
                        >
                          {study.case_label}
                        </Box>
                      </Box>
                      <CardContent
                        sx={{
                          p: 5,
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          {getIconForType(study.case_label)}
                          <Typography
                            variant="subtitle2"
                            component="div"
                            sx={{ color: "#9CA3AF", fontWeight: 600 }}
                          >
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm, remarkBreaks]}
                            >
                              {study.client_name}
                            </ReactMarkdown>
                          </Typography>
                        </Stack>
                        <Typography
                          variant="h4"
                          component="div"
                          sx={{
                            color: "white",
                            fontWeight: 800,
                            mb: 2,
                            fontFamily: "Philosopher",
                          }}
                        >
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkBreaks]}
                          >
                            {study.title}
                          </ReactMarkdown>
                        </Typography>
                        <Typography
                          variant="body1"
                          component="div"
                          sx={{
                            color: "#9CA3AF",
                            mb: 3,
                            lineHeight: 1.7,
                            flexGrow: 1,
                          }}
                        >
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkBreaks]}
                          >
                            {study.excerpt}
                          </ReactMarkdown>
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mb: 4,
                          }}
                        >
                          {study.technologies?.map((tech) => (
                            <Chip
                              key={tech.technology?.id}
                              label={tech.technology?.name}
                              size="small"
                              sx={{
                                bgcolor: "rgba(255,255,255,0.05)",
                                color: "#D4AF37",
                                border: "1px solid rgba(212, 175, 55, 0.2)",
                              }}
                            />
                          ))}
                        </Box>
                        <Divider
                          sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 3 }}
                        />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#9CA3AF",
                                display: "block",
                                textTransform: "uppercase",
                                letterSpacing: 1,
                              }}
                            >
                              {t("services.caseStudies.resultLabel")}
                            </Typography>
                            <Typography
                              variant="h6"
                              component="div"
                              sx={{ color: "#0FA3A3", fontWeight: 800 }}
                            >
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm, remarkBreaks]}
                              >
                                {study.result}
                              </ReactMarkdown>
                            </Typography>
                          </Box>
                          <Button
                            endIcon={<LaunchIcon />}
                            component={Link}
                            to={`/services/${study.slug}`}
                            sx={{
                              color: "#D4AF37",
                              fontWeight: 700,
                              "&:hover": {
                                bgcolor: "transparent",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {t("services.caseStudies.viewCase")}
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesPage;
